import React, { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { dates, validators, formats } from "investira.sdk";
import { displays } from "investira.react.lib";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Stack,
  TextField,
  LoadingButton,
  MenuItem,
  InputAdornment,
  Typography,
  useMediaQuery,
  Chip,
} from "investira.react.components";
import { ATIVO_MERCADO } from "@investirapri/financa.js/lib/enums";
import { financeiro } from "@investirapri/financa.js/lib/operacoes";
import {
  DatePicker,
  DecimalTextField,
  InputAsync,
  PessoaImage,
} from "../../components/molecules";
import { VALIDATIONS } from "../../const";
import { useCarteirasContext } from "../../contexts/CarteirasContext";
import services from "../../services";
import withResponseHandling from "../../hoc/withResponseHandling";

const DialogAddAtivo = memo((props) => {
  const { state, actions } = useCarteirasContext();
  const params = useParams();

  const [ativobase, setAtivoBase] = useState({});

  const mercadosStored = useSelector((store) => store.mercados.data);
  const mercados = useMemo(
    () =>
      Object.values(mercadosStored).filter((pMercado) => {
        return ![
          "CNT",
          "POU",
          "FGT",
          "CRP",
          "PRV",
          "IMV",
          "VEI",
          "CAR",
          "MOE",
          "MTL",
          "BEM",
          "CBL",
          "DRI",
        ].includes(pMercado.id);
      }),
    [mercadosStored]
  );

  const isEdit = !validators.isEmpty(state.posicaoSelected);
  const isOpen = state.isDialogOpen && state.dialogType === "new";

  const isUpMd = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const xInitialValues = {
    pu: 1,
    quantidade: 100,
    isin: "",
    mercado: state.posicaoSelected.mercado || props.mercado || "ACN",
    ativo_id: state.posicaoSelected.ativo_id,
    ifeed_ativo_id: state.posicaoSelected.ifeed_ativo_id || null,
    taxa: 0,
    vencimento: dayjs(),
    operacao_data: dayjs(),
    operacao_indicador_percentual: 100,
    data: state.data_posicao ? dayjs(state.data_posicao) : dayjs(),
  };

  const VALIDATION_SCHEMA = Yup.object().shape({
    //data: Yup.date().max(new Date()).required(VALIDATIONS.REQUIRED),
    pu: Yup.number().required(VALIDATIONS.REQUIRED),
    quantidade: Yup.number().required(VALIDATIONS.REQUIRED),
    mercado: Yup.string().required(VALIDATIONS.REQUIRED),
    ifeed_ativo_id: Yup.number().required(VALIDATIONS.REQUIRED),
  });

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      const xValues = {
        ...pValues,
        // pu: pValues.pu,
        data: dayjs(pValues.data).startOf("day").format(),
        financeiro: financeiro({
          quantidade: pValues.quantidade,
          pu: pValues.pu,
          lote: ativobase.lote,
        }),
      };

      const xRequest = isEdit ? actions.updateAtivo : actions.addAtivo;

      xRequest(
        {
          carteira_id: params.id,
          data: dates.toSqlDate(new Date()),
          ...xValues,
        },
        (rRes) => {
          const xAtivo = rRes.data;
          pActions.resetForm();
          actions.handleMergePosicoes(xAtivo, isEdit);
          actions.handleNewPosicaoCancel();
          props.onCreate({
            mercado: xAtivo.mercado,
            ativo_tipo_id: xAtivo.ativo_tipo_id,
          });
        },
        null,
        () => {
          pActions.setSubmitting(false);
        }
      );
    },
  });

  // const xDefaultValue = useMemo(
  //   () => ({ isin: formik.values.isin }),
  //   [formik.values.isin]
  // );

  const handleChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const handleNext = (pDate) => {
    actions.handleSetDataPosicao(pDate);
  };

  const handleAsyncChange = (pValues) => {
    if (validators.isNull(pValues)) {
      return;
    }

    const {
      ativo,
      ativo_id = null,
      ifeed_ativo_id,
      ativo_tipo_id,
      isin,
      mercado,
      ticker,
    } = pValues;

    formik.setValues({
      ...formik.values,
      ativo,
      ativo_id,
      ifeed_ativo_id,
      ativo_tipo_id,
      isin,
      mercado,
      ticker,
    });

    setAtivoBase(pValues);
  };

  const handleClose = () => {
    formik.setSubmitting(false);
    formik.resetForm();
    setAtivoBase({});
    actions.handleNewPosicaoCancel();
  };

  function renderOption(pProps, pOption) {
    if (validators.isNull(pOption)) {
      return;
    }

    const {
      ativo_id,
      ativo,
      ticker,
      logo_pessoa_id,
      pessoa,
      logo_ok,
      ativo_seuid,
      isin,
      mercado,
      serie,
      emissor_cnpj,
      vencimento,
    } = pOption;

    return (
      <Stack
        {...pProps}
        id={ativo_id}
        key={crypto.randomUUID()}
        direction="row"
        flexGrow={1}
        spacing={3}
        alignItems="flex-start"
        component="li"
        sx={{
          px: 2,
          py: 2,
          cursor: "pointer",
          "&:hover": {
            color: "gray",
            backgroundColor: "background.main",
          },
          borderBottom: "1px solid",
          borderBottomColor: "background.main",
        }}
      >
        <Stack>
          <PessoaImage
            logoPessoaId={logo_pessoa_id}
            alt={pessoa}
            hasLogo={logo_ok}
            size={36}
          />
        </Stack>
        <Stack flexGrow={1}>
          <Typography
            variant="body2"
            color="textPrimary"
            sx={{ fontWeight: 600 }}
          >
            {ticker || ativo_seuid || ativo}
          </Typography>
          <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                display: { xs: "none", sm: "inline-block" },
              }}
            >
              {ativo || isin}
            </Typography>
          </Stack>

          <Stack direction="row" sx={{ gap: "8px 8px" }} flexWrap="wrap">
            {mercado && (
              <Chip
                label={ATIVO_MERCADO[mercado].descricao}
                size="small"
                color="primary"
                sx={{ display: { xs: "flex", md: "none" } }}
              />
            )}

            {emissor_cnpj && (
              <Chip
                label={displays.cnpj(emissor_cnpj)}
                size="small"
                color="secondary"
              />
            )}

            {isin && <Chip label={isin} size="small" color="secondary" />}
            {vencimento && (
              <Chip
                label={`Venc.: ${formats.formatDate(vencimento)}`}
                size="small"
                color="secondary"
              />
            )}
            {serie && (
              <Chip label={`Série: ${serie}`} size="small" color="secondary" />
            )}
          </Stack>
        </Stack>
        {isUpMd && (
          <Stack>
            <Stack justifyContent="end">
              {mercado && (
                <Chip
                  label={ATIVO_MERCADO[mercado].descricao}
                  size="small"
                  color="primary"
                />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  }

  function hasTaxa(pMercado) {
    return ["TPR", "TBA", "TPU"].includes(pMercado);
  }

  function hasVencimento(pMercado) {
    return ["TPR", "TBA"].includes(pMercado);
  }

  function hasOperacaoData(pMercado) {
    return ["TPR", "TBA", "TPU"].includes(pMercado);
  }

  function hasOperacaoPercentual(pMercado) {
    return ["TPR", "TBA", "TPU"].includes(pMercado);
  }

  function hasAtivo(pAtivoBase) {
    return !validators.isEmpty(pAtivoBase);
  }

  useEffect(() => {
    // EDIT
    if (isOpen && isEdit) {
      const {
        pu,
        quantidade,
        isin,
        mercado,
        ativo_id,
        ifeed_ativo_id,
        taxa,
        vencimento,
        operacao_data,
        operacao_indicador_percentual,
        data,
      } = state.posicaoSelected;

      const xValues = {
        pu,
        quantidade,
        isin,
        mercado,
        ativo_id,
        ifeed_ativo_id,
        taxa,
        operacao_indicador_percentual,
        vencimento: dayjs(vencimento),
        operacao_data: dayjs(operacao_data),
        data: dayjs(data),
      };

      formik.setValues(xValues);
      setAtivoBase(state.posicaoSelected);
    }

    if (isOpen && !isEdit) {
      formik.setValues(xInitialValues);
    }
  }, [isOpen, isEdit, state.posicaoSelected]);

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth onClose={handleClose}>
      <DialogTitle>{isEdit ? `Editar Ativo` : `Adicionar Ativo`}</DialogTitle>
      {validators.isEmpty(state.data_posicao) ? (
        <>
          <DialogContent>
            <Stack sx={{ marginBottom: 2, py: 1 }} spacing={2}>
              <Stack flex="1">
                <DatePicker
                  label="Data Posição"
                  name="data"
                  onChange={(pValue) => {
                    handleChange("data", pValue);
                  }}
                  value={formik.values.data}
                  error={Boolean(formik.errors.data)}
                  helperText={formik.errors.data}
                  disabled={formik.isSubmitting}
                  fullWidth
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              type="button"
              color="primary"
              onClick={handleClose}
            >
              Cancelar
            </Button>
            <Button
              variant="outlined"
              type="button"
              color="primary"
              onClick={() => handleNext(formik.values.data)}
            >
              Continuar
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <form noValidate onSubmit={formik.handleSubmit}>
            <DialogContent>
              <Stack sx={{ marginBottom: 2, py: 1 }} spacing={2}>
                {!isEdit && (
                  <Stack direction="row" spacing={2}>
                    <Stack flexGrow={1}>
                      <TextField
                        label="Mercado"
                        id="mercado"
                        name="mercado"
                        variant="outlined"
                        select
                        value={formik.values.mercado}
                        onChange={(e) => {
                          formik.handleChange(e);
                          handleChange("ifeed_ativo_id", null);
                          setAtivoBase({});
                        }}
                      >
                        {mercados.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.descricao}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Stack>
                )}

                {!isEdit && (
                  <Stack direction="row" spacing={2} flex="1">
                    <Stack flex="1">
                      <InputAsync
                        label="Ativo"
                        name="ativo_id"
                        id="ativo_id"
                        messageError="Falha na busca de ativos"
                        //defaultValue={xDefaultValue}
                        refreshInput={formik.values.mercado}
                        service={services.ativosbase.list}
                        serviceParams={{
                          sort: "isin desc",
                          mercado: formik.values.mercado,
                        }}
                        renderOptionKey="ativo_seuid"
                        onChange={(_e, pValues) => {
                          handleAsyncChange(pValues);
                        }}
                        onInputChange={() => []}
                        getOptionKey={(pOption) => {
                          return pOption["ativo_id"];
                        }}
                        renderOption={renderOption}
                        getOptionLabel={(option) => {
                          return !validators.isNull(option?.ativo)
                            ? option.ativo
                            : "";
                        }}
                        disabled={formik.isSubmitting}
                      />
                    </Stack>
                  </Stack>
                )}

                {hasAtivo(ativobase) && (
                  <Stack flex="1" direction="row" spacing={2}>
                    <Stack flex="1">
                      <TextField
                        id="quantidade"
                        label="Quantidade"
                        name="quantidade"
                        type="text"
                        value={formik.values.quantidade}
                        variant="outlined"
                        onChange={(e) => {
                          handleChange("quantidade", Number(e.target.value));
                        }}
                        error={Boolean(formik.errors.quantidade)}
                        helperText={formik.errors.quantidade}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          pattern: "[0-9]*",
                          decimal: 2,
                          formNoValidate: "formnovalidate",
                          maxLength: 9,
                        }}
                      />
                    </Stack>

                    <Stack flex="1">
                      {/* <ExcelLikeInput
                        id="pu"
                        name="pu"
                        label="PU"
                        variant="outlined"
                        value={formik.values.pu}
                        onChange={(pValue) => {
                          handleChange("pu", pValue);
                        }}
                        error={Boolean(formik.errors.pu)}
                        helperText={formik.errors.pu}
                        disabled={formik.isSubmitting}
                        decimal={ativobase.decimais}
                        fullWidth
                      /> */}

                      <DecimalTextField
                        id="pu"
                        name="pu"
                        label="PU"
                        variant="outlined"
                        value={formik.values.pu}
                        placeholder={"100,00"}
                        onChange={(_, pValue) => handleChange("pu", pValue)}
                        allowNegative={false}
                        decimal={ativobase.decimais}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(formik.errors.pu)}
                        helperText={formik.errors.pu}
                        disabled={formik.isSubmitting}
                        fullWidth
                        InputProps={{
                          endAdornment: null,
                        }}
                      />
                      {/* <TextFieldCurrency
                        id="pu"
                        label="PU"
                        name="pu"
                        value={formik.values.pu}
                        onChange={(_, pValue) => handleChange("pu", pValue)}
                        error={Boolean(formik.errors.pu)}
                        helperText={formik.errors.pu}
                        inputProps={{
                          decimal: ativobase.decimais,
                        }}
                        fullWidth
                        variant="outlined"
                      /> */}
                    </Stack>
                  </Stack>
                )}

                {hasAtivo(ativobase) && (
                  <Stack flex="1" flexGrow={1} direction="row" spacing={2}>
                    <Stack flex="1">
                      <DecimalTextField
                        id="financeiro"
                        label="Financeiro"
                        name="financeiro"
                        type="text"
                        value={financeiro({
                          quantidade: formik.values.quantidade,
                          pu: formik.values.pu,
                          lote: ativobase.lote,
                        })}
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          readonly: true,
                        }}
                        InputProps={{
                          endAdornment: null,
                        }}
                      />
                    </Stack>
                  </Stack>
                )}

                {(hasTaxa(formik.values.mercado) ||
                  hasOperacaoData(formik.values.mercado) ||
                  hasOperacaoPercentual(formik.values.mercado)) && (
                  <Stack
                    component="fieldset"
                    flex="1"
                    direction="row"
                    py={2}
                    px={0}
                    border="none"
                  >
                    <Typography
                      component="legend"
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Compra
                    </Typography>
                    <Stack flex="1" direction="row" spacing={2}>
                      {hasTaxa(formik.values.mercado) && (
                        <Stack flex="1">
                          <DecimalTextField
                            id="taxa"
                            name="taxa"
                            label="Taxa"
                            variant="outlined"
                            value={formik.values.taxa}
                            placeholder={"100,00"}
                            onChange={(_, pValue) =>
                              handleChange("taxa", pValue)
                            }
                            allowNegative={false}
                            decimal={4}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  %
                                </InputAdornment>
                              ),
                            }}
                            error={Boolean(formik.errors.taxa)}
                            helperText={formik.errors.taxa}
                            disabled={formik.isSubmitting}
                            fullWidth
                          />
                        </Stack>
                      )}
                      {hasOperacaoData(formik.values.mercado) && (
                        <Stack flex="1">
                          <DatePicker
                            label="Data"
                            name="operacao_data"
                            onChange={(pValue) => {
                              handleChange("operacao_data", pValue);
                            }}
                            value={formik.values.operacao_data}
                            error={Boolean(formik.errors.operacao_data)}
                            helperText={formik.errors.operacao_data}
                            disabled={formik.isSubmitting}
                            fullWidth
                          />
                        </Stack>
                      )}
                      {hasOperacaoPercentual(formik.values.mercado) && (
                        <Stack flex="1">
                          <DecimalTextField
                            id="operacao_indicador_percentual"
                            name="operacao_indicador_percentual"
                            label="Indicador Percentual"
                            variant="outlined"
                            value={formik.values.operacao_indicador_percentual}
                            placeholder={"100,00"}
                            onChange={(_, pValue) =>
                              handleChange(
                                "operacao_indicador_percentual",
                                pValue
                              )
                            }
                            allowNegative={false}
                            decimal={2}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  %
                                </InputAdornment>
                              ),
                            }}
                            error={Boolean(
                              formik.errors.operacao_indicador_percentual
                            )}
                            helperText={
                              formik.errors.operacao_indicador_percentual
                            }
                            disabled={formik.isSubmitting}
                            fullWidth
                          />
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                )}

                {/* <Stack
                  alignItems="center"
                  justifyContent="flex-end"
                  direction="row"
                  spacing={2}
                >
                  <Button
                    variant="outlined"
                    type="button"
                    color="primary"
                    onClick={handleClose}
                  >
                    Cancelar
                  </Button>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting || formik.initialValid}
                  >
                    {isEdit ? "Alterar" : "Adicionar"}
                  </LoadingButton>
                </Stack> */}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                type="button"
                color="primary"
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {isEdit ? "Alterar" : "Adicionar"}
              </LoadingButton>
            </DialogActions>
          </form>
        </>
      )}
    </Dialog>
  );
});

DialogAddAtivo.displayName = "DialogAddAtivo";

export default withResponseHandling(DialogAddAtivo);
